import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { Plus, Search, Users, X, ChevronLeft, ChevronRight, Download, FileSpreadsheet, Pencil, Trash2, Eye, UserCheck, UserX, Upload, XCircle } from 'lucide-react';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BLANK_FORM = {
  first_name: '', last_name: '', date_of_birth: '', gender: 'male',
  class_id: '', roll_number: '', blood_group: '', address: '',
  parent_name: '', parent_email: '', parent_phone: '', parent_relation: 'father',
};

export default function StudentManagement() {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const [students, setStudents] = useState([]);
  const [classes, setClasses] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [viewStudent, setViewStudent] = useState(null);
  const [search, setSearch] = useState('');
  const [filterClass, setFilterClass] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [csvUploading, setCsvUploading] = useState(false);
  const [csvResult, setCsvResult] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [form, setForm] = useState(BLANK_FORM);
  const [photoUrls, setPhotoUrls] = useState({});
  const [photoUploading, setPhotoUploading] = useState(false);

  const fetchStudents = async () => {
    try {
      const params = new URLSearchParams({ page, limit: 20, status: '' });
      if (filterClass) params.set('class_id', filterClass);
      if (search) params.set('search', search);
      params.delete('status');
      const res = await axios.get(`${API}/students?${params}`, { headers });
      const list = res.data.data.students || [];
      setStudents(list);
      setTotal(res.data.data.total || 0);
      // Load photos for students that have one
      const urls = {};
      await Promise.all(list.filter(s => s.photo_path).map(async s => {
        try {
          const pr = await axios.get(`${API}/files/${s.photo_path}`, { headers, responseType: 'blob' });
          urls[s.student_id] = URL.createObjectURL(pr.data);
        } catch {}
      }));
      setPhotoUrls(prev => ({ ...prev, ...urls }));
    } catch { toast.error('Failed to load students'); }
    setLoading(false);
  };

  const handlePhotoUpload = async (e, studentId) => {
    const file = e.target.files[0];
    if (!file) return;
    setPhotoUploading(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      await axios.post(`${API}/students/${studentId}/photo`, fd, { headers: { ...headers, 'Content-Type': 'multipart/form-data' } });
      // Reload photo
      const pr = await axios.get(`${API}/students/${studentId}`, { headers });
      const path = pr.data.data.photo_path;
      if (path) {
        const img = await axios.get(`${API}/files/${path}`, { headers, responseType: 'blob' });
        setPhotoUrls(prev => ({ ...prev, [studentId]: URL.createObjectURL(img.data) }));
        setViewStudent(vs => vs ? { ...vs, photo_path: path } : vs);
      }
      toast.success('Photo uploaded');
    } catch { toast.error('Upload failed'); }
    finally { setPhotoUploading(false); e.target.value = ''; }
  };

  const handlePhotoRemove = async (studentId) => {
    try {
      await axios.delete(`${API}/students/${studentId}/photo`, { headers });
      setPhotoUrls(prev => { const n = { ...prev }; delete n[studentId]; return n; });
      setViewStudent(vs => vs ? { ...vs, photo_path: null } : vs);
      toast.success('Photo removed');
    } catch { toast.error('Failed to remove photo'); }
  };

  const fetchClasses = async () => {
    try {
      const res = await axios.get(`${API}/classes`, { headers });
      setClasses(res.data.data || []);
    } catch {}
  };

  const fetchStats = async () => {
    try {
      const res = await axios.get(`${API}/students/stats/overview`, { headers });
      setStats(res.data.data);
    } catch {}
  };

  useEffect(() => { fetchClasses(); fetchStats(); }, []);
  useEffect(() => { fetchStudents(); }, [page, filterClass, search]);

  const openAdd = () => { setForm(BLANK_FORM); setEditId(null); setShowForm(true); };
  const openEdit = (s) => {
    setForm({
      first_name: s.first_name || '', last_name: s.last_name || '',
      date_of_birth: s.date_of_birth || '', gender: s.gender || 'male',
      class_id: s.class_id || '', roll_number: s.roll_number || '',
      blood_group: s.blood_group || '', address: s.address || '',
      parent_name: s.parent_name || '', parent_email: s.parent_email || '',
      parent_phone: s.parent_phone || '', parent_relation: s.parent_relation || 'father',
    });
    setEditId(s.student_id);
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editId) {
        await axios.put(`${API}/students/${editId}`, form, { headers });
        toast.success('Student updated');
      } else {
        await axios.post(`${API}/students`, form, { headers });
        toast.success('Student enrolled successfully');
      }
      setShowForm(false);
      setEditId(null);
      setForm(BLANK_FORM);
      fetchStudents(); fetchStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  const handleDelete = (s) => {
    setConfirm({
      title: 'Delete Student',
      message: `Delete ${s.full_name}? This cannot be undone.`,
      onConfirm: async () => {
        try {
          await axios.delete(`${API}/students/${s.student_id}`, { headers });
          toast.success('Student deleted');
          fetchStudents(); fetchStats();
        } catch (err) { toast.error(err.response?.data?.detail || 'Failed to delete'); }
      }
    });
  };

  const handleToggleStatus = async (s) => {
    const newStatus = s.status === 'active' ? 'inactive' : 'active';
    try {
      await axios.put(`${API}/students/${s.student_id}`, { status: newStatus }, { headers });
      toast.success(`Student ${newStatus === 'active' ? 'activated' : 'deactivated'}`);
      fetchStudents();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  const handleCsvUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setCsvUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/students/bulk-upload`, formData, { headers: { ...headers, 'Content-Type': 'multipart/form-data' } });
      setCsvResult(res.data.data);
      toast.success(`Enrolled ${res.data.data.created} students`);
      fetchStudents(); fetchStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setCsvUploading(false);
      e.target.value = '';
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const res = await axios.get(`${API}/students/bulk-template`, { headers, responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a'); a.href = url; a.download = 'students_template.xlsx'; a.click();
    } catch { toast.error('Failed to download template'); }
  };

  const totalPages = Math.ceil(total / 20);

  return (
    <div className="space-y-6" data-testid="student-management-page">
      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <p className="text-xs text-slate-400 font-medium">Total Students</p>
            <p className="text-2xl font-bold text-slate-900" data-testid="total-students">{stats.total_students}</p>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <p className="text-xs text-slate-400 font-medium">Total Classes</p>
            <p className="text-2xl font-bold text-slate-900">{stats.total_classes}</p>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <p className="text-xs text-slate-400 font-medium">Boys</p>
            <p className="text-2xl font-bold text-blue-600">{stats.gender?.male || 0}</p>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <p className="text-xs text-slate-400 font-medium">Girls</p>
            <p className="text-2xl font-bold text-pink-600">{stats.gender?.female || 0}</p>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <h1 className="text-2xl font-bold text-slate-900">Students</h1>
        <div className="flex items-center gap-2 w-full sm:w-auto flex-wrap">
          <div className="relative flex-1 sm:w-56">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input placeholder="Search by name or ID..." value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} className="pl-9 h-9" />
          </div>
          <Select value={filterClass} onValueChange={(v) => { setFilterClass(v === 'all' ? '' : v); setPage(1); }}>
            <SelectTrigger className="w-36 h-9"><SelectValue placeholder="All Classes" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Classes</SelectItem>
              {classes.map(c => <SelectItem key={c.class_id} value={c.class_id}>{c.name} {c.section}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button variant="outline" className="h-9 border-slate-200 text-slate-600" onClick={handleDownloadTemplate}>
            <Download className="h-4 w-4 mr-1" /> Template
          </Button>
          <label className="cursor-pointer">
            <input type="file" accept=".csv,.xlsx,.xls" className="hidden" onChange={handleCsvUpload} />
            <span className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-200 text-slate-600 text-sm font-medium hover:bg-slate-50 h-9">
              <FileSpreadsheet className="h-4 w-4" /> {csvUploading ? 'Uploading...' : 'Import CSV'}
            </span>
          </label>
          <Button onClick={openAdd} className="bg-teal-500 hover:bg-teal-600 text-white h-9">
            <Plus className="h-4 w-4 mr-1" /> Enroll
          </Button>
        </div>
      </div>

      {/* Add / Edit Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-2xl shadow-xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-lg font-bold">{editId ? 'Edit Student' : 'Enroll New Student'}</h2>
              <button onClick={() => setShowForm(false)}><X className="h-5 w-5 text-slate-400" /></button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <p className="text-xs text-slate-400 font-semibold uppercase tracking-wider">Student Details</p>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs text-slate-500">First Name *</Label><Input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} required /></div>
                <div><Label className="text-xs text-slate-500">Last Name *</Label><Input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} required /></div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div><Label className="text-xs text-slate-500">Date of Birth *</Label><Input type="date" value={form.date_of_birth} onChange={(e) => setForm({ ...form, date_of_birth: e.target.value })} required /></div>
                <div>
                  <Label className="text-xs text-slate-500">Gender *</Label>
                  <Select value={form.gender} onValueChange={(v) => setForm({ ...form, gender: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="male">Male</SelectItem>
                      <SelectItem value="female">Female</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label className="text-xs text-slate-500">Blood Group</Label><Input value={form.blood_group} onChange={(e) => setForm({ ...form, blood_group: e.target.value })} placeholder="e.g. A+" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-slate-500">Class *</Label>
                  <Select value={form.class_id} onValueChange={(v) => setForm({ ...form, class_id: v })}>
                    <SelectTrigger><SelectValue placeholder="Select class" /></SelectTrigger>
                    <SelectContent>{classes.map(c => <SelectItem key={c.class_id} value={c.class_id}>{c.name} {c.section}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label className="text-xs text-slate-500">Roll Number</Label><Input value={form.roll_number} onChange={(e) => setForm({ ...form, roll_number: e.target.value })} /></div>
              </div>
              <div><Label className="text-xs text-slate-500">Address</Label><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>

              <p className="text-xs text-slate-400 font-semibold uppercase tracking-wider pt-2">Parent / Guardian Details</p>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs text-slate-500">Parent Name</Label><Input value={form.parent_name} onChange={(e) => setForm({ ...form, parent_name: e.target.value })} /></div>
                <div>
                  <Label className="text-xs text-slate-500">Relation</Label>
                  <Select value={form.parent_relation} onValueChange={(v) => setForm({ ...form, parent_relation: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="father">Father</SelectItem>
                      <SelectItem value="mother">Mother</SelectItem>
                      <SelectItem value="guardian">Guardian</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs text-slate-500">Parent Email</Label><Input type="email" value={form.parent_email} onChange={(e) => setForm({ ...form, parent_email: e.target.value })} /></div>
                <div><Label className="text-xs text-slate-500">Parent Phone</Label><Input value={form.parent_phone} onChange={(e) => setForm({ ...form, parent_phone: e.target.value })} /></div>
              </div>

              <div className="flex gap-2 pt-3">
                <Button type="button" variant="outline" onClick={() => setShowForm(false)} className="flex-1">Cancel</Button>
                <Button type="submit" className="flex-1 bg-teal-500 hover:bg-teal-600 text-white">{editId ? 'Update Student' : 'Enroll Student'}</Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Modal */}
      {viewStudent && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md shadow-xl">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-lg font-bold">Student Details</h2>
              <button onClick={() => setViewStudent(null)}><X className="h-5 w-5 text-slate-400" /></button>
            </div>
            <div className="flex items-center gap-4 mb-5">
              <div className="relative group">
                {photoUrls[viewStudent.student_id] ? (
                  <img src={photoUrls[viewStudent.student_id]} alt={viewStudent.full_name} className="h-16 w-16 rounded-full object-cover border-2 border-slate-200" />
                ) : (
                  <div className={`h-16 w-16 rounded-full flex items-center justify-center text-lg font-bold text-white ${viewStudent.gender === 'male' ? 'bg-blue-400' : viewStudent.gender === 'female' ? 'bg-pink-400' : 'bg-slate-400'}`}>
                    {viewStudent.first_name?.[0]}{viewStudent.last_name?.[0]}
                  </div>
                )}
                <div className="absolute -bottom-1 -right-1 flex gap-0.5">
                  <label className="cursor-pointer h-6 w-6 rounded-full bg-teal-500 flex items-center justify-center hover:bg-teal-400 shadow" title="Upload photo">
                    <input type="file" accept="image/*" className="hidden" onChange={e => handlePhotoUpload(e, viewStudent.student_id)} disabled={photoUploading} />
                    {photoUploading ? <div className="h-3 w-3 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Upload className="h-3 w-3 text-white" />}
                  </label>
                  {photoUrls[viewStudent.student_id] && (
                    <button onClick={() => handlePhotoRemove(viewStudent.student_id)} className="h-6 w-6 rounded-full bg-red-400 flex items-center justify-center hover:bg-red-500 shadow" title="Remove photo">
                      <XCircle className="h-3 w-3 text-white" />
                    </button>
                  )}
                </div>
              </div>
              <div>
                <h3 className="font-bold text-slate-900 text-lg">{viewStudent.full_name}</h3>
                <p className="text-xs text-slate-400 font-mono">{viewStudent.admission_number}</p>
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-bold mt-1 ${viewStudent.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`}>
                  {viewStudent.status === 'active' ? 'Active' : 'Inactive'}
                </span>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3 text-sm">
              {[
                ['Class', `${viewStudent.class_name} ${viewStudent.section}`],
                ['Roll No.', viewStudent.roll_number || '-'],
                ['Date of Birth', viewStudent.date_of_birth || '-'],
                ['Gender', viewStudent.gender || '-'],
                ['Blood Group', viewStudent.blood_group || '-'],
                ['Address', viewStudent.address || '-'],
                ['Parent', viewStudent.parent_name || '-'],
                ['Relation', viewStudent.parent_relation || '-'],
                ['Parent Phone', viewStudent.parent_phone || '-'],
                ['Parent Email', viewStudent.parent_email || '-'],
              ].map(([label, val]) => (
                <div key={label}>
                  <p className="text-[10px] text-slate-400 uppercase tracking-wider">{label}</p>
                  <p className="text-slate-700 font-medium truncate">{val}</p>
                </div>
              ))}
            </div>
            <Button className="w-full mt-5 bg-teal-500 text-white rounded-xl" onClick={() => setViewStudent(null)}>Close</Button>
          </div>
        </div>
      )}

      {/* Table */}
      {loading ? (
        <div className="text-center py-12 text-slate-400">Loading students...</div>
      ) : students.length === 0 ? (
        <div className="text-center py-16 bg-slate-50 rounded-2xl border border-dashed border-slate-200">
          <Users className="h-12 w-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500 font-medium">No students enrolled yet</p>
          <p className="text-sm text-slate-400 mt-1">Start by adding classes, then enroll students</p>
        </div>
      ) : (
        <>
          <div className="overflow-x-auto bg-white rounded-xl border border-slate-200">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-100">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500">Name</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500">Adm No.</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500">Class</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500">Roll</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500">DOB</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500">Gender</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500">Parent</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500">Contact</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500">Status</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {students.map((s) => (
                  <tr key={s.student_id} className="hover:bg-slate-50/50">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2.5">
                        {photoUrls[s.student_id] ? (
                          <img src={photoUrls[s.student_id]} alt={s.full_name} className="h-8 w-8 rounded-full object-cover flex-shrink-0 border border-slate-200" />
                        ) : (
                          <div className={`h-8 w-8 rounded-full flex items-center justify-center text-xs font-bold text-white flex-shrink-0 ${s.gender === 'male' ? 'bg-blue-400' : s.gender === 'female' ? 'bg-pink-400' : 'bg-slate-400'}`}>
                            {s.first_name?.[0]}{s.last_name?.[0]}
                          </div>
                        )}
                        <span className="font-medium text-sm text-slate-900 whitespace-nowrap">{s.full_name}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-xs font-mono text-slate-500 whitespace-nowrap">{s.admission_number}</td>
                    <td className="px-4 py-3 text-sm text-slate-700 whitespace-nowrap">{s.class_name} {s.section}</td>
                    <td className="px-4 py-3 text-sm text-slate-500">{s.roll_number || '-'}</td>
                    <td className="px-4 py-3 text-xs text-slate-500 whitespace-nowrap">{s.date_of_birth || '-'}</td>
                    <td className="px-4 py-3 text-sm text-slate-500 capitalize">{s.gender}</td>
                    <td className="px-4 py-3 text-sm text-slate-600 whitespace-nowrap">{s.parent_name || '-'}</td>
                    <td className="px-4 py-3 text-xs text-slate-400 whitespace-nowrap">{s.parent_phone || s.parent_email || '-'}</td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => handleToggleStatus(s)}
                        className={`inline-flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] font-semibold transition-colors ${s.status === 'active' ? 'bg-emerald-50 text-emerald-600 hover:bg-emerald-100' : 'bg-rose-50 text-rose-500 hover:bg-rose-100'}`}
                        title={s.status === 'active' ? 'Click to deactivate' : 'Click to activate'}
                      >
                        {s.status === 'active' ? <UserCheck className="h-3 w-3" /> : <UserX className="h-3 w-3" />}
                        {s.status === 'active' ? 'Active' : 'Inactive'}
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <button onClick={() => setViewStudent(s)} className="p-1.5 rounded-lg hover:bg-slate-100" title="View details">
                          <Eye className="h-3.5 w-3.5 text-slate-400" />
                        </button>
                        <button onClick={() => openEdit(s)} className="p-1.5 rounded-lg hover:bg-slate-100" title="Edit student">
                          <Pencil className="h-3.5 w-3.5 text-slate-400" />
                        </button>
                        <button onClick={() => handleDelete(s)} className="p-1.5 rounded-lg hover:bg-red-50" title="Delete student">
                          <Trash2 className="h-3.5 w-3.5 text-red-400" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between">
              <p className="text-sm text-slate-400">Showing {(page - 1) * 20 + 1}–{Math.min(page * 20, total)} of {total}</p>
              <div className="flex gap-1">
                <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}><ChevronLeft className="h-4 w-4" /></Button>
                <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}><ChevronRight className="h-4 w-4" /></Button>
              </div>
            </div>
          )}
        </>
      )}

      {/* CSV Result Dialog */}
      {csvResult && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-sm shadow-xl">
            <h2 className="text-lg font-bold mb-4">Import Results</h2>
            <div className="flex gap-4 mb-4">
              <div className="flex-1 bg-emerald-50 rounded-xl p-3 text-center">
                <p className="text-2xl font-bold text-emerald-600">{csvResult.created}</p>
                <p className="text-xs text-emerald-600">Enrolled</p>
              </div>
              <div className="flex-1 bg-amber-50 rounded-xl p-3 text-center">
                <p className="text-2xl font-bold text-amber-600">{csvResult.skipped}</p>
                <p className="text-xs text-amber-600">Skipped</p>
              </div>
            </div>
            {csvResult.errors?.length > 0 && (
              <div className="max-h-36 overflow-y-auto space-y-1 mb-4">
                {csvResult.errors.map((e, i) => (
                  <p key={i} className="text-xs text-red-500">Row {e.row}: {e.error}</p>
                ))}
              </div>
            )}
            <Button className="w-full bg-teal-500 text-white rounded-xl" onClick={() => setCsvResult(null)}>Done</Button>
          </div>
        </div>
      )}

      <ConfirmDialog confirm={confirm} onClose={() => setConfirm(null)} />
    </div>
  );
}
